import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import React, { useEffect, useState } from "react";

import { parseDate } from "../../helpers/date";
import { useGetPatientClaimsQuery } from "../../store/queries/claims";
import {
  CATEGORY_LABELS,
  DENIAL_STATUS_LABELS,
  DenialStatus,
  IPatientClaimRow,
  RESOLUTION_STATUS_COLORS,
  RESOLUTION_STATUS_LABELS,
  ResolutionStatus,
} from "../../types/claim";
import { formatCurrency } from "../../utils/claimAdjustments";
import { countOtherPatientClaims } from "./patientClaims";

interface IPatientClaimsTabProps {
  claimId: string;
  onNavigateToClaim?: (claimId: string) => void;
  /** False while the modal is hidden — the fetch is skipped. */
  active: boolean;
}

// The claims-list Category filter (PAYMENT_TYPE_OPTIONS) words these as plurals ("Underpayments");
// a tag on one claim takes the singular of the same wording.
const PAYMENT_TYPE_TAGS: Record<
  string,
  { label: string; severity: "danger" | "warning" | "success" }
> = {
  UNDERPAYMENT: { label: "Underpayment", severity: "danger" },
  OVERPAYMENT: { label: "Overpayment", severity: "warning" },
  CORRECT: { label: "Correct Payment", severity: "success" },
};

// PrimeReact 8's Tag has no "secondary" severity and an unstyled Tag paints in the brand colour,
// so the neutral tags set their gray explicitly.
const secondaryTagStyle: React.CSSProperties = {
  background: "#6c757d",
  color: "#ffffff",
};

const humanizeEnum = (value: string): string =>
  value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

// Same wording as the modal's Remit Type row (its map is module-private there, and importing the
// modal from here would be circular).
const CLAIM_FORM_TYPE_LABELS: Record<string, string> = {
  INSTITUTIONAL: "Institutional (UB-04)",
  PROFESSIONAL: "Professional (CMS-1500)",
  UNKNOWN: "Unknown form type",
};

const formatServiceDate = (iso: string | null): string =>
  iso ? parseDate(iso) : "-";

const formatClassification = (row: IPatientClaimRow): string => {
  const parts = [
    row.denialClassificationCode,
    row.denialClassificationTitle,
  ].filter((part): part is string => !!part);
  return parts.length > 0 ? parts.join(" — ") : "-";
};

// Same figure as the claims list's Variance column: the size of the gap as a share of the
// expected amount. The sign lives in the Difference value shown beside it.
const formatVariance = (row: IPatientClaimRow): string => {
  if (
    row.expectedAmount === null ||
    row.expectedAmount === 0 ||
    row.amountDifference === null
  ) {
    return "-";
  }
  const percentage =
    (Math.abs(row.amountDifference) / row.expectedAmount) * 100;
  return `${percentage.toFixed(1)}%`;
};

const formatCategory = (category: string | null): string =>
  (category && CATEGORY_LABELS[category]) || "-";

const formatDenialStatus = (denialStatus: string | null): string =>
  (denialStatus && DENIAL_STATUS_LABELS[denialStatus as DenialStatus]) || "-";

function PaymentTypeTag({ paymentType }: { paymentType: string | null }) {
  if (!paymentType) return null;
  const known = PAYMENT_TYPE_TAGS[paymentType];
  if (known) return <Tag value={known.label} severity={known.severity} />;
  return <Tag value={humanizeEnum(paymentType)} style={secondaryTagStyle} />;
}

function ResolutionStatusTag({
  resolutionStatus,
}: {
  resolutionStatus: string | null;
}) {
  if (!resolutionStatus) return null;
  const status = resolutionStatus as ResolutionStatus;
  const label = RESOLUTION_STATUS_LABELS[status];
  if (!label) {
    return (
      <Tag value={humanizeEnum(resolutionStatus)} style={secondaryTagStyle} />
    );
  }
  const severity = RESOLUTION_STATUS_COLORS[status];
  // NO_APPEAL_NEEDED has no severity ("default gray" in RESOLUTION_STATUS_COLORS).
  return (
    <Tag
      value={label}
      severity={severity}
      style={severity ? undefined : secondaryTagStyle}
    />
  );
}

function DetailItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: "0.75rem", color: "#6c757d" }}>{label}</div>
      <div
        style={{
          fontSize: "0.85rem",
          color: "#212529",
          overflowWrap: "anywhere",
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * CLE-2837 — the claim modal's fifth tab: every claim of the same patient, so from any one claim
 * Ani can see all the others (Ani: "from any of these claims I want to see all the other claims of
 * the same patient").
 *
 * One collapsed card per claim, newest service date first, modelled on the Event Timeline. The
 * header line carries what she scans by — date, claim number, payer, Billed / Allowed / Expected /
 * Paid and the status tags — so she rarely needs to expand; the body holds the rest and the
 * "Open claim" button that swaps the modal to that claim (the modal keeps this tab selected).
 */
function PatientClaimsTab({
  claimId,
  onNavigateToClaim,
  active,
}: IPatientClaimsTabProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // `currentData`, not `data`: this tab stays open while the modal navigates between claims, and
  // `data` would keep showing the previous claim's list (with the wrong row marked "This claim")
  // until the new one arrives.
  const { currentData, isFetching, isError } = useGetPatientClaimsQuery(
    claimId,
    { skip: !claimId || !active }
  );

  // Every claim opens with all cards collapsed.
  useEffect(() => {
    setExpanded({});
  }, [claimId]);

  const toggle = (id: string) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  if (isError) {
    return (
      <div style={{ padding: "1rem" }}>
        <Message
          severity="warn"
          text="Unable to load this patient's claims. Please try again later."
          style={{ width: "100%" }}
        />
      </div>
    );
  }

  if (!currentData) {
    if (!isFetching) return null;
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <ProgressSpinner style={{ width: "30px", height: "30px" }} />
      </div>
    );
  }

  const rows = currentData.data;
  const otherCount = countOtherPatientClaims(rows);

  let summary: string;
  if (rows.length === 0) {
    summary =
      "This claim carries no patient identifier, so related claims cannot be found.";
  } else if (otherCount === 0) {
    summary = "No other claims for this patient";
  } else {
    summary = `${otherCount} other claim${
      otherCount === 1 ? "" : "s"
    } for this patient`;
  }

  return (
    <div style={{ padding: "1rem" }}>
      <div
        style={{
          marginBottom: "0.75rem",
          fontSize: "14px",
          color: "#495057",
        }}
      >
        {summary}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        {rows.map((row) => {
          const isOpen = !!expanded[row.id];
          const payer = row.clearPayerName ?? row.claimPayerName ?? "-";
          const showOpen = !!onNavigateToClaim && !row.isCurrent;
          return (
            <div
              key={row.id}
              data-testid={`patient-claim-${row.id}`}
              style={{
                border: row.isCurrent
                  ? "1px solid #3b82f6"
                  : "1px solid #e0e0e0",
                borderLeft: row.isCurrent
                  ? "4px solid #3b82f6"
                  : "1px solid #e0e0e0",
                borderRadius: "4px",
                background: row.isCurrent ? "#eef5ff" : "#fafafa",
              }}
            >
              <button
                type="button"
                onClick={() => toggle(row.id)}
                aria-expanded={isOpen}
                style={{
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  padding: "0.75rem 1rem",
                  textAlign: "left",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "1rem",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "0.75rem",
                    minWidth: 0,
                  }}
                >
                  <span style={{ fontWeight: 600, minWidth: "5.5rem" }}>
                    {formatServiceDate(row.serviceDate)}
                  </span>
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: "0.85rem",
                      color: "#666",
                    }}
                  >
                    {row.claimNumber || "-"}
                  </span>
                  <span style={{ fontSize: "0.85rem", color: "#333" }}>
                    {payer}
                  </span>
                  <PaymentTypeTag paymentType={row.paymentType} />
                  <ResolutionStatusTag
                    resolutionStatus={row.resolutionStatus}
                  />
                  {row.combinedClaimNumbers.length > 0 && (
                    <Tag
                      value={`Combined +${row.combinedClaimNumbers.length}`}
                      severity="info"
                      title={`Combined with: ${row.combinedClaimNumbers.join(
                        ", "
                      )}`}
                      style={{ fontSize: "0.7rem" }}
                    />
                  )}
                  {row.isCurrent && (
                    <Tag value="This claim" style={secondaryTagStyle} />
                  )}
                </span>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1.5rem",
                    fontSize: "0.85rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span>
                    Billed: <strong>{formatCurrency(row.chargeAmount)}</strong>
                  </span>
                  <span>
                    Allowed: <strong>{formatCurrency(row.claimAmount)}</strong>
                  </span>
                  <span>
                    Expected:{" "}
                    <strong>{formatCurrency(row.expectedAmount)}</strong>
                  </span>
                  <span>
                    Paid: <strong>{formatCurrency(row.totalPaid)}</strong>
                  </span>
                  <span>{isOpen ? "▾" : "▸"}</span>
                </span>
              </button>
              {isOpen && (
                <div
                  style={{
                    borderTop: "1px solid #e0e0e0",
                    padding: "0.75rem 1rem",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(200px, 1fr))",
                      gap: "0.75rem 1.5rem",
                    }}
                  >
                    <DetailItem label="Claim type">
                      {(row.claimFormType &&
                        CLAIM_FORM_TYPE_LABELS[row.claimFormType]) ||
                        "-"}
                    </DetailItem>
                    <DetailItem label="Case signature">
                      {row.caseSignature || "-"}
                    </DetailItem>
                    <DetailItem label="Classification">
                      {formatClassification(row)}
                    </DetailItem>
                    <DetailItem label="Category">
                      {formatCategory(row.category)}
                    </DetailItem>
                    <DetailItem label="Denial status">
                      {formatDenialStatus(row.denialStatus)}
                    </DetailItem>
                    <DetailItem label="Difference">
                      {formatCurrency(row.amountDifference)}
                    </DetailItem>
                    <DetailItem label="Variance">
                      {formatVariance(row)}
                    </DetailItem>
                    <DetailItem label="Events">{row.eventCount}</DetailItem>
                    <DetailItem label="Combined claims">
                      {row.combinedClaimNumbers.length > 0
                        ? row.combinedClaimNumbers.join(", ")
                        : "-"}
                    </DetailItem>
                  </div>
                  {showOpen && (
                    <div style={{ marginTop: "0.75rem" }}>
                      <Button
                        label="Open claim"
                        icon="pi pi-external-link"
                        className="p-button-sm p-button-outlined"
                        onClick={() => onNavigateToClaim?.(row.id)}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PatientClaimsTab;
